import { Injectable } from '@angular/core';
import {
  arrayToMap,
  BackendResponse,
  mapToArray,
  StorageV2Service,
  StoreV2Map,
  User,
  UserV2Service,
} from '@schenck/rotec-components';
import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { MachineApi } from 'src/api/machine.api';
import { MachineEvent } from 'src/utils/enums/machine.enum';
import { Machine } from 'src/utils/interfaces/machine.interfaces';
import { ErrorHandlerService } from '../error-handler/error-handler.service';

@Injectable()
export class MachineService extends StoreV2Map<Machine> {
  private _events$ = new Subject<MachineEvent>();
  /**
   * Emits whenever a settings event occurs.
   */
  public events$ = this._events$.asObservable();
  private loaded$ = new ReplaySubject(1);

  /**
   * Returns available machines after they are loaded from backend.
   */
  public availableMachines$: Observable<Machine[]> = this.loaded$.pipe(
    switchMap(() => this.state$),
    map((machines) => mapToArray(machines).filter((machine) => machine.available))
  );

  /**
   * Returns machines after they are loaded from backend.
   */
  public machines$: Observable<Machine[]> = this.loaded$.pipe(
    switchMap(() => this.state$),
    map((machines) => mapToArray(machines))
  );

  constructor(
    private machineApi: MachineApi,
    private storageService: StorageV2Service,
    private userService: UserV2Service,
    private errorHandlerService: ErrorHandlerService
  ) {
    super('MachineService', { storageService, name: 'MachineService' });
    this.userService.currentUser$.pipe(filter((user: User) => !!user)).subscribe(() => {
      this.loadMachines$();
    });
  }

  /**
   * Load machines from backend.
   */
  public loadMachines$() {
    combineLatest([this.machineApi.get$(), this.machineApi.getMachines$()]).subscribe(
      ([available, all]) => {
        if (BackendResponse.isSuccess(available) && BackendResponse.isSuccess(all)) {
          const availableMachinesMap = arrayToMap(available.result, 'assetId');
          //DIG-19496 Available machine is greater than all machine
          const machineResultArr = available.result.length > all.result.length ? available : all;
          const allMachines = machineResultArr.result.map((machine) => ({
            ...machine,
            available: machine.assetId in availableMachinesMap,
          }));
          this.setState('got machines', arrayToMap(allMachines, 'assetId'));
          this.loaded$.next(null);
        }

        if (!BackendResponse.isSuccess(available)) {
          this.errorHandlerService.setAppError(true);
          throw available.error;
        }

        if (!BackendResponse.isSuccess(all)) {
          this.errorHandlerService.setAppError(true);
          throw all.error;
        }
      }
    );
  }
}
